import { createAsyncThunk } from "@reduxjs/toolkit";
import { getError } from "../../../services/lib/getError";
import { getAllStorageObjects } from "../../../services/api/instances/storageApi/methods";
import { filterByDfAgentUri } from "../utils/filterByDfAgentUri";
import { ExtraAppValue } from "../../../services/api/instances/extraAppApi/types";
import { StorageObject } from "../../../services/api/instances/storageApi/types/object_types";
import { APP_DEERFLOW_API_INSTANCES } from "../../../services/api/constants";
import { createApiInstance } from "../../../services/api/instances/deerflowApi/instance";
import { RootState } from "../../../services/redux/types";
import { deerFlowApi } from "../../../services/api/instances/deerflowApi/methods";
import { TDeerFlowThread } from "../../../services/api/instances/deerflowApi/types";
import { createNotification } from "../../../app/state/notificationSlice/slice";
import { title } from "process";

export const generateDeerFlowInstances = createAsyncThunk(
  "deerflow/generateDeerFlowInstances",
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const apps = await getAllStorageObjects("ai_agents_extra_app");
      const deerFlowApps = filterByDfAgentUri(
        apps as StorageObject<ExtraAppValue>[],
      );
      const { deerFlowInstances } = (getState() as RootState).deerFlowState;
      const hasChanged =
        JSON.stringify(deerFlowInstances) !== JSON.stringify(deerFlowApps);
      if (hasChanged) {
        APP_DEERFLOW_API_INSTANCES.clear();
        deerFlowApps.forEach((instance) => {
          APP_DEERFLOW_API_INSTANCES.set(
            instance.url,
            createApiInstance(instance.url),
          );
        });
      }
      return deerFlowApps;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getDeerFlowInstanceAgents = createAsyncThunk(
  "deerflow/getDeerFlowInctanceAgents",
  async (url: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const instance = APP_DEERFLOW_API_INSTANCES.get(url);
      if (!instance) return;

      const agents = await deerFlowApi.getFlowsAgent(instance);

      return agents;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getDeerFlowInstanceThreads = createAsyncThunk(
  "deerflow/getDeerFlowInctanceThreads",
  async (url: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const instance = APP_DEERFLOW_API_INSTANCES.get(url);
      if (!instance) return;

      const threads = await deerFlowApi.getFlowInstanceThreads(instance);

      return threads;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const createDeerFlowInstanceThread = createAsyncThunk(
  "deerflow/createDeerFlowInstanceThread",
  async (agentName: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const { selectedDeerFlowInstance } = (getState() as RootState)
        .deerFlowState;
      if (!selectedDeerFlowInstance) return;
      const instance = APP_DEERFLOW_API_INSTANCES.get(
        selectedDeerFlowInstance.url,
      );
      if (!instance) return;

      const thread = await deerFlowApi.createFlowInstanceThreads(
        instance,
        agentName,
      );
      dispatch(
        createNotification("success", `Создан чат с агентом ${agentName}`),
      );

      return { ...thread, values: { title: "Новый чат (временное название)" } };
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteDeerFlowInstanceThread = createAsyncThunk(
  "deerflow/deleteDeerFlowInstanceThread",
  async (thread: TDeerFlowThread, { dispatch, getState, rejectWithValue }) => {
    try {
      const { selectedDeerFlowInstance } = (getState() as RootState)
        .deerFlowState;
      if (!selectedDeerFlowInstance || !thread) return;
      const instance = APP_DEERFLOW_API_INSTANCES.get(
        selectedDeerFlowInstance.url,
      );
      if (!instance) return;

      await deerFlowApi.deleteFlowInstanceThreads(instance, thread.thread_id);
      dispatch(createNotification("success", `Тред успешно удален`));

      return thread.thread_id;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getDeerFlowThreadMessages = createAsyncThunk(
  "deerflow/getDeerFlowThreadMessages",
  async (thread: TDeerFlowThread, { dispatch, getState, rejectWithValue }) => {
    try {
      const { selectedDeerFlowInstance } = (getState() as RootState)
        .deerFlowState;
      const instance = APP_DEERFLOW_API_INSTANCES.get(
        selectedDeerFlowInstance?.url ?? "",
      );
      if (!instance) return;

      const messages = await deerFlowApi.getFlowInstanceMessages(
        instance,
        thread.thread_id,
      );

      return messages;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const postDeerFlowThreadMessage = createAsyncThunk(
  "deerflow/postDeerFlowThreadMessage",
  async (text: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const { selectedDeerFlowInstance, selectedInstanceThread } = (
        getState() as RootState
      ).deerFlowState;
      const instance = APP_DEERFLOW_API_INSTANCES.get(
        selectedDeerFlowInstance?.url ?? "",
      );
      if (!instance || !selectedInstanceThread) return;

      await deerFlowApi.sendMessage(
        instance,
        text,
        selectedInstanceThread?.thread_id,
      );
      const messages = await deerFlowApi.getFlowInstanceMessages(
        instance,
        selectedInstanceThread.thread_id,
      );

      return messages.at(-1);
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
